import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { pricingPlanMessages } from 'src/features/pricing/messages';
import { PricingPlanObjectFormSchemaValues } from 'src/features/pricing/sites/utils/formSchema';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';

type PricingPlanFormProps = {
    form: UseFormReturn<PricingPlanObjectFormSchemaValues>;
};

function ObjectPricingPlanForm({ form }: PricingPlanFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    // Standard (default) plans are reserved for copying only, so exclude them from selection here.
    // Only published plans can be assigned to an object; drafts are not selectable.
    const { data: pricingPlansData } = useListPricingPlans({
        currently_active: true,
        is_system: false,
        status: PRICING_PLAN_STATUS.PUBLISHED,
        parkingId,
    });

    const pricingPlanOptions = React.useMemo(() => {
        if (!pricingPlansData) return [];
        return pricingPlansData.results.map((plan) => {
            const translatedPlan = plan.translations.find((p) => p.language === locale);

            return { value: plan.identity, label: translatedPlan?.name || '' };
        });
    }, [pricingPlansData, locale]);

    return (
        <Form form={form} variant="dialog">
            <SelectFormField<PricingPlanObjectFormSchemaValues>
                form={form}
                name="pricing_plan_identity"
                label={formatMessage(pricingPlanMessages.pricingPlan)}
                options={pricingPlanOptions}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingPlanMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value ?? ''} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default ObjectPricingPlanForm;
